// AutomationRuleDetails.jsx

import React from 'react';

// Utilities
import {
    AUTOMATION_TRIGGER_TYPE_OPTIONS,
    buildDefaultCondition,
    normalizeAutomationTriggerType,
} from '../utils/automationRuleUtils';

// Components
import Divider from 'components/divider/Divider';
import ModalField from 'components/modal/field/ModalField';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * AutomationRuleDetails
 *
 * Renders the title, description, and trigger inputs for an automation rule.
 *
 * @param {object} rule - The automation rule being edited.
 * @param {array} ruleFieldOptions - Available fields for trigger and condition selection.
 * @param {boolean} isCollapsed - Whether the rule body is currently collapsed.
 * @param {function} updateRule - Updates a rule by key with an updater callback.
 *
 * @returns {ReactNode} The rendered automation rule details block.
 */
const AutomationRuleDetails = ({
    rule,
    ruleFieldOptions,
    isCollapsed,
    updateRule,
}) => {
    const triggerType = normalizeAutomationTriggerType(rule?.trigger?.type);
    const isRecordCreated = triggerType === 'record_created';
    const defaultFieldKey = String(ruleFieldOptions?.[0]?.key || '').trim();

    // ----- HANDLERS ----- //

    /**
     * handleTriggerTypeChange
     *
     * Switches between field_changed and record_created triggers.
     *
     * @param {object} event - Trigger type select change event.
     */
    const handleTriggerTypeChange = (event) => {
        const nextType = normalizeAutomationTriggerType(event.target.value);

        updateRule(rule.key, (prev) => {
            if (nextType === 'record_created') {
                return {
                    ...prev,
                    trigger: {
                        type: 'record_created',
                        fieldKey: '',
                    },
                };
            }

            const nextFieldKey = String(
                prev?.trigger?.fieldKey || defaultFieldKey || '',
            ).trim();

            return {
                ...prev,
                trigger: {
                    type: 'field_changed',
                    fieldKey: nextFieldKey,
                },
            };
        });
    };

    /**
     * handleTriggerFieldChange
     *
     * Updates the trigger field and keeps the first condition aligned with it.
     *
     * @param {object} event - Trigger field select change event.
     */
    const handleTriggerFieldChange = (event) => {
        const nextFieldKey = event.target.value;
        updateRule(rule.key, (prev) => ({
            ...prev,
            trigger: {
                type: 'field_changed',
                fieldKey: nextFieldKey,
            },
            conditionGroups: (Array.isArray(prev.conditionGroups)
                ? prev.conditionGroups
                : []
            ).map((group, groupIndex) => {
                if (groupIndex !== 0) return group;

                const currentConditions = Array.isArray(group.conditions)
                    ? group.conditions
                    : [];
                if (!currentConditions.length) return group;

                const firstCondition =
                    currentConditions[0] ||
                    buildDefaultCondition({ defaultFieldKey: nextFieldKey });
                return {
                    ...group,
                    conditions: [
                        {
                            ...firstCondition,
                            fieldKey: firstCondition?.fieldKey || nextFieldKey,
                        },
                        ...currentConditions.slice(1),
                    ],
                };
            }),
        }));
    };

    // ----- RENDER ----- //

    return (
        <>
            {/* TITLE */}
            <ModalField>
                <ModalLabel label="Title" />
                <TextInput
                    placeholder='e.g. "When Implemented"'
                    type="text"
                    value={rule.title || ''}
                    onChange={(event) => {
                        const nextTitle = event.target.value;
                        updateRule(rule.key, (prev) => ({ ...prev, title: nextTitle }));
                    }}
                />
            </ModalField>

            {/* DESCRIPTION */}
            <ModalField>
                <ModalLabel label="Description" />
                <TextInput
                    multiline
                    expandable
                    placeholder='e.g. "Set Date Implemented and send a notification to the team when Status is set to Implemented."'
                    type="text"
                    value={rule.description || ''}
                    onChange={(event) => {
                        const nextDescription = event.target.value;
                        updateRule(rule.key, (prev) => ({ ...prev, description: nextDescription }));
                    }}
                />
            </ModalField>

            {!isCollapsed && (
                <>
                    <Divider />

                    {/* TRIGGER TYPE */}
                    <ModalField>
                        <ModalLabel label="Trigger" />
                        <SelectInput
                            value={triggerType}
                            options={AUTOMATION_TRIGGER_TYPE_OPTIONS}
                            onChange={handleTriggerTypeChange}
                        />
                    </ModalField>

                    {/* TRIGGER FIELD (field_changed only) */}
                    {!isRecordCreated && (
                        <ModalField>
                            <ModalLabel label="When this changes..." />
                            <SelectInput
                                style={{
                                    fontSize: '12px',
                                }}
                                value={rule?.trigger?.fieldKey || ''}
                                options={ruleFieldOptions.map((field) => ({
                                    value: field.key,
                                    label: field.label,
                                }))}
                                placeholder="Select field..."
                                onChange={handleTriggerFieldChange}
                            />
                        </ModalField>
                    )}
                </>
            )}
        </>
    );
};

// Dev Tools
AutomationRuleDetails.displayName = 'AutomationRuleDetails';

export default AutomationRuleDetails;
